import toast from "react-hot-toast";
import { STORAGE_KEYS } from "./constant";
import { getItem, setItem, removeItem } from "./storage";

/**
 * Toast style object
 */
const toastStyle = {
  fontSize: "14px",
  padding: "12px 24px",
  borderRadius: "8px",
};

/**
 * Display error toast message
 * @param message - The error message to display
 */
export const showErrorToast = (message: string) => {
  toast.dismiss();
  toast.error(message, {
    style: toastStyle,
    duration: 4000,
  });
};

/**
 * Display success toast message
 * @param message - The success message to display
 */
export const showSuccessToast = (message: string) => {
  toast.dismiss();
  toast.success(message, {
    style: toastStyle,
    duration: 3000,
  });
};

/**
 * Display info toast message
 * @param message - The info message to display
 */
export const showInfoToast = (message: string) => {
  toast(message, {
    style: toastStyle,
    duration: 3000,
  });
};

/**
 * Format date to readable string
 * @param date - Date string or Date object
 * @param format - Format type (default: "full")
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date,
  format: "full" | "short" | "time" = "full"
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  switch (format) {
    case "full":
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    case "short":
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    case "time":
      return dateObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    default:
      return dateObj.toLocaleDateString();
  }
};

/**
 * Get authentication token from secure storage
 * @returns Access token or null
 */
export const getAuthToken = (): string | null => {
  return getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Set authentication token in secure storage
 * @param token - Access token
 */
export const setAuthToken = (token: string): void => {
  setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
};

/**
 * Set refresh token in secure storage
 * @param token - Refresh token
 */
export const setRefreshToken = (token: string): void => {
  setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
};

/**
 * Get refresh token from secure storage
 * @returns Refresh token or null
 */
export const getRefreshToken = (): string | null => {
  return getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Set user info in secure storage (encrypted by secure-ls).
 */
export const setUserInfo = (userInfo: {
  userId: string;
  email: string;
  fullName: string;
  profileImage?: string;
}): void => {
  setItem(STORAGE_KEYS.USER_INFO, userInfo);
};

/**
 * Get user info from secure storage
 * @returns User info object or null
 */
export const getUserInfo = (): {
  userId: string;
  email: string;
  fullName: string;
  profileImage?: string;
} | null => {
  return getItem<{
    userId: string;
    email: string;
    fullName: string;
    profileImage?: string;
  }>(STORAGE_KEYS.USER_INFO);
};

/**
 * Clear all user session data from local storage.
 * Also clears Bearer tokens (current backend); HttpOnly cookies are cleared server-side on logout.
 */
export const removeAuthToken = (): void => {
  removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  removeItem(STORAGE_KEYS.USER_INFO);
};

/**
 * Check if user is authenticated.
 * Checks Bearer token (current backend) or user profile presence (cookie-based backend).
 * Actual token validity is enforced server-side on the first API call.
 * @returns Boolean indicating whether a user session is present
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken() || !!getUserInfo();
};

/**
 * Truncate text to specified length
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

/**
 * Capitalize first letter of each word
 * @param text - Text to capitalize
 * @returns Capitalized text
 */
export const capitalizeWords = (text: string): string => {
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Validate email format
 * @param email - Email to validate
 * @returns Boolean indicating if email is valid
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Prevent special characters on keydown, allowing spaces
 * @param e - Keyboard event
 */
export const preventSpecialCharactersAllowSpace = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const key = e.key;
  // Allow: backspace, delete, tab, escape, enter, space, and alphanumeric
  if (
    ["Backspace", "Delete", "Tab", "Escape", "Enter", " "].includes(key) ||
    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    ((e.ctrlKey || e.metaKey) &&
      ["a", "c", "v", "x", "A", "C", "V", "X"].includes(key)) ||
    // Allow: home, end, left, right
    ["Home", "End", "ArrowLeft", "ArrowRight"].includes(key)
  ) {
    return;
  }
  // Prevent if not alphanumeric or space
  if (!/^[a-zA-Z0-9 ]$/.test(key)) {
    e.preventDefault();
  }
};

/**
 * Prevent special characters on paste, allowing spaces
 * @param e - Clipboard event
 */
export const preventSpecialCharactersPasteAllowSpace = (
  e: React.ClipboardEvent<HTMLInputElement>
) => {
  const pastedText = e.clipboardData.getData("text");
  // Only allow alphanumeric characters and spaces
  if (!/^[a-zA-Z0-9 ]*$/.test(pastedText)) {
    e.preventDefault();
  }
};

/**
 * Get initials from a full name (up to 2 characters)
 * @param name - Full name string
 */
export const getInitials = (name: string | undefined): string =>
  (name ?? "")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const Helper = {
  showErrorToast,
  showSuccessToast,
  showInfoToast,
  formatDate,
  getAuthToken,
  setAuthToken,
  setRefreshToken,
  getRefreshToken,
  setUserInfo,
  getUserInfo,
  removeAuthToken,
  isAuthenticated,
  truncateText,
  capitalizeWords,
  isValidEmail,
  debounce,
  preventSpecialCharactersAllowSpace,
  preventSpecialCharactersPasteAllowSpace,
};

export default Helper;
